import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { SuccessReportTypes } from "../../../utils/ErrorTypes";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import {
  DeliveryOptions,
  OrderTypesEnum,
  PAYMENT_METHOD,
} from "../../../utils/enums";
import {
  OfferType,
  PointingSystem,
  ScheduledSlot,
  SubItemType,
  outOfStockItemsDetails,
  reduxCartAndLoyalty,
} from "../../../utils/types";
import { linkUserOffers } from "../reduxHelpers/LoyaltyReduxHelper";
import { checkFranchiseCart } from "../reduxHelpers/cartReduxHelper";
import { OfferPayload } from "../types/CartLoyaltyV2Types";
import { transformObject } from "../types/TypeHelper";

const initialState: reduxCartAndLoyalty = {
  cart: {
    franchiseId: -1,
    items: [],
    totalPrice: 0,
  },
  cartItemsNumber: 0,
  allOrderTypesoffersArray: [],
  offersArray: [],
  redeemedOffer: undefined,
  offerErrorReason: undefined,
  remainingToEligiable: 0,
  isEligibleOffer: false,
  needsRedirect: -1,
  hasRoundingMethodAndLevel: false,
  paymentMethod: PAYMENT_METHOD.ONLINE_CARD,
  tip: 0,
  pointingSystem: undefined,
  customDeliveryTime: 0,
  promoCode: {
    name: "",
    valid: true,
    applied: false,
  },
  selectedScheduledOptions: {
    selectedSlot: undefined,
    selectedScheduledOption: undefined,
    numberOfGuests: 0,
  },
  outdatedSubItems: [],
  isFirstTimeOrder: undefined,
  isTwistOfferAvailable: undefined,
  selectedPickupTime: "",
  outOfStockItemsDetails: undefined,
};

const cartAndLoyalty = createSlice({
  name: "cartAndLoyalty",
  initialState,
  reducers: {
    setPaymentMethod: (state, { payload }) => {
      state.paymentMethod = payload;
    },

    setReceiptNote: (state, action: PayloadAction<string | undefined>) => {
      state.cart.receiptNote = action.payload;
    },

    setPromoCode: (state, action) => {
      state.promoCode = action.payload;
    },
    checkFranchiseCartRedux: (state, action: PayloadAction<number>) => {
      checkFranchiseCart(state, action.payload);
    },
    linkUserOffersRedux: (
      state,
      action: PayloadAction<{
        offersArray: OfferType[];
        orderType: OrderTypesEnum;
        isFirstTimeOrder?: boolean;
        isTwistOfferAvailable?: boolean;
        redeemedOffer?: Offer;
      }>
    ) => {
      linkUserOffers(
        state,
        action.payload.offersArray,
        action.payload.orderType,
        action.payload.isFirstTimeOrder,
        action.payload.isTwistOfferAvailable,
        action.payload.redeemedOffer
      );
    },
    setOffersArray: (
      state,
      action: PayloadAction<{
        offersArray: Offer[];
      }>
    ) => {
      state.offersArray = action.payload.offersArray as OfferType[];
    },
    resetAllOffersArrays: (state) => {
      state.offersArray = [];
      state.allOrderTypesoffersArray = [];
      state.isFirstTimeOrder = undefined;
    },
    setHasRoundingMethodAndLevel: (state, action: PayloadAction<boolean>) => {
      state.hasRoundingMethodAndLevel = action.payload;
    },
    setPointingSystem: (
      state,
      action: PayloadAction<PointingSystem | undefined>
    ) => {
      state.pointingSystem = action.payload;
    },
    setSelectedScheduleSlot: (
      state,
      action: PayloadAction<ScheduledSlot | undefined>
    ) => {
      state.selectedScheduledOptions = {
        ...state.selectedScheduledOptions,
        selectedSlot: action.payload,
      };
    },
    setSelectedDeliveryOption: (
      state,
      action: PayloadAction<DeliveryOptions | undefined>
    ) => {
      state.selectedScheduledOptions = {
        ...state.selectedScheduledOptions,
        selectedScheduledOption: action.payload,
      };
    },
    setScheduleNumberOfGuests: (state, action: PayloadAction<number>) => {
      state.selectedScheduledOptions = {
        ...state.selectedScheduledOptions,
        numberOfGuests: action.payload,
      };
    },
    resetSelectedScheduleOptions: (state) => {
      state.selectedScheduledOptions = {
        selectedSlot: undefined,
        selectedScheduledOption: undefined,
        numberOfGuests: 0,
      };
    },
    setOfferErrorReason: (state, action: PayloadAction<SuccessReportTypes>) => {
      state.offerErrorReason = action.payload;
    },
    removeOfferErrorReason: (state) => {
      state.offerErrorReason = undefined;
    },
    setOutdatedSubItems: (state, action: PayloadAction<SubItemType[]>) => {
      state.outdatedSubItems = action.payload;
    },
    resetOutdatedSubItems: (state) => {
      state.outdatedSubItems = [];
    },
    shiftOffersArray: (
      state,
      action: PayloadAction<OfferPayload | undefined>
    ) => {
      const offer = action.payload;
      if (!offer) {
        return;
      }
      const offerIndex: number = state.offersArray.findIndex((offerSlider) =>
        (transformObject(offerSlider) as Offer).isEqual(offer)
      );
      if (
        state.offersArray &&
        state.offersArray.length > 1 &&
        offerIndex > -1
      ) {
        state.offersArray.unshift(state.offersArray.splice(offerIndex, 1)[0]);
      }
    },
    setSelectedPickupTime(state, action) {
      state.selectedPickupTime = action.payload;
    },
    updateOutOfStockItemsDetails: (
      state,
      action: PayloadAction<outOfStockItemsDetails>
    ) => {
      state.outOfStockItemsDetails = action.payload;
    },
  },
});

export const {
  setOffersArray,
  resetAllOffersArrays,
  linkUserOffersRedux,
  checkFranchiseCartRedux,
  setHasRoundingMethodAndLevel,
  setReceiptNote,
  setPaymentMethod,
  setPointingSystem,
  setPromoCode,
  setSelectedScheduleSlot,
  setSelectedDeliveryOption,
  setScheduleNumberOfGuests,
  setOfferErrorReason,
  removeOfferErrorReason,
  setOutdatedSubItems,
  resetOutdatedSubItems,
  shiftOffersArray,
  setSelectedPickupTime,
  updateOutOfStockItemsDetails,
  resetSelectedScheduleOptions,
} = cartAndLoyalty.actions;

export default cartAndLoyalty.reducer;
